import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { authService } from '@/services/authService';
import type { LoginCredentials, SignUpData } from '@/types';
import toast from 'react-hot-toast';

export function useAuth() {
  const navigate = useNavigate();
  const {
    user,
    isAuthenticated,
    isLoading,
    login: storeLogin,
    logout: storeLogout,
    setLoading,
    updateUser,
  } = useAuthStore();

  const googleButtonRef = useRef<HTMLDivElement>(null);

  // Initialize Google Sign-In
  useEffect(() => {
    const initGoogle = async () => {
      try {
        await authService.loadGoogleScript();
        authService.initializeGoogleSignIn(
          googleButtonRef.current,
          (u, t) => {
            storeLogin(u, t);
            navigate('/closet');
            toast.success(`Welcome, ${u.displayName}!`);
          },
          (error) => toast.error(error),
        );
      } catch {
        /* Google Sign-In optional */
      }
    };
    initGoogle();
  }, [storeLogin, navigate]);

  // Initialize Apple Sign-In
  useEffect(() => {
    const initApple = async () => {
      try {
        await authService.loadAppleScript();
        await authService.initializeAppleSignIn();
      } catch {
        /* Apple Sign-In optional */
      }
    };
    initApple();
  }, []);

  const loginWithEmail = useCallback(
    async (credentials: LoginCredentials) => {
      setLoading(true);
      try {
        const { user: u, tokens } = await authService.login(credentials);
        storeLogin(u, tokens);
        navigate('/closet');
        toast.success(`Welcome back, ${u.displayName}!`);
      } catch {
        toast.error('Invalid email or password');
      } finally {
        setLoading(false);
      }
    },
    [storeLogin, navigate, setLoading],
  );

  const signUpWithEmail = useCallback(
    async (signUpData: SignUpData) => {
      setLoading(true);
      try {
        const { user: u, tokens } = await authService.signUp(signUpData);
        storeLogin(u, tokens);
        navigate('/closet');
        toast.success('Account created successfully!');
      } catch {
        toast.error('Failed to create account. Email may already exist.');
      } finally {
        setLoading(false);
      }
    },
    [storeLogin, navigate, setLoading],
  );

  const signInWithApple = useCallback(async () => {
    setLoading(true);
    try {
      const { user: u, tokens } = await authService.signInWithApple();
      storeLogin(u, tokens);
      navigate('/closet');
      toast.success(`Welcome, ${u.displayName}!`);
    } catch {
      toast.error('Apple sign-in failed');
    } finally {
      setLoading(false);
    }
  }, [storeLogin, navigate, setLoading]);

  const logout = useCallback(() => {
    storeLogout();
    navigate('/login');
    toast.success('Signed out');
  }, [storeLogout, navigate]);

  const updateProfile = useCallback(
    async (updates: Parameters<typeof updateUser>[0]) => {
      try {
        const updated = await authService.updateProfile(updates);
        updateUser(updated);
        toast.success('Profile updated');
      } catch {
        toast.error('Failed to update profile');
      }
    },
    [updateUser],
  );

  return {
    user,
    isAuthenticated,
    isLoading,
    googleButtonRef,
    loginWithEmail,
    signUpWithEmail,
    signInWithApple,
    logout,
    updateProfile,
  };
}
